'use client';

import { ChevronDown, ChevronRight } from 'lucide-react';
import type React from 'react';
import { useCallback, useState } from 'react';

// Fields to use for collapsed preview, in order of preference
const PREVIEW_FIELDS = ['summary', 'name', 'title', 'id', 'date', 'type'];

/**
 * Caller-supplied hook for customizing an object node's collapsed preview. Return a non-empty
 * string to collapse that node and display the string as its preview, overriding
 * `defaultExpandLevel`; return `null` to preserve the default preview/expansion behavior. Invoked
 * only for non-empty object nodes (never for arrays, primitives, `null`, or empty objects).
 */
type JsonPreviewFn = (value: Readonly<Record<string, unknown>>) => string | null;

interface JsonViewerProps {
  data: unknown;
  defaultExpandLevel?: number;
  className?: string;
  previewFn?: JsonPreviewFn | undefined;
}

interface JsonNodeProps {
  keyName?: string;
  value: unknown;
  level: number;
  defaultExpandLevel: number;
  previewFn?: JsonPreviewFn | undefined;
}

/** Extracts a human-readable preview string from an object by checking a priority list of common summary fields. */
function getPreview(value: Readonly<Record<string, unknown>>): string | null {
  for (const field of PREVIEW_FIELDS) {
    if (field in value && typeof value[field] === 'string') {
      return value[field] as string;
    }
  }
  return null;
}

/** Recursively renders a single JSON value — null, array, object, string, number, boolean, or unknown — with expand/collapse for collections. */
function JsonNode({ keyName, value, level, defaultExpandLevel, previewFn }: JsonNodeProps) {
  const isPreviewableObject =
    value != null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.keys(value).length > 0;
  const customPreview = isPreviewableObject
    ? previewFn?.(value as Readonly<Record<string, unknown>>) || null
    : null;
  const [expanded, setExpanded] = useState(
    customPreview != null ? false : level < defaultExpandLevel
  );

  /** Toggles the expanded/collapsed state of this collection node when the user clicks its header row. */
  const toggle = useCallback(() => setExpanded((previous) => !previous), []);

  const indent = level * 16;

  // Null / Undefined
  if (value == null) {
    return (
      <div style={{ paddingLeft: indent }} className="flex items-center py-0.5">
        <span className="text-muted-foreground mr-1">-</span>
        {keyName && <span className="text-purple-600 dark:text-purple-400">{keyName}: </span>}
        <span className="text-gray-500">null</span>
      </div>
    );
  }

  // Array
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return (
        <div style={{ paddingLeft: indent }} className="flex items-center py-0.5">
          <span className="text-muted-foreground mr-1">-</span>
          {keyName && <span className="text-purple-600 dark:text-purple-400">{keyName}: </span>}
          <span className="text-gray-500">[]</span>
        </div>
      );
    }

    return (
      <div>
        <button
          type="button"
          style={{ paddingLeft: indent }}
          className="flex items-center py-0.5 cursor-pointer hover:bg-muted/50 rounded w-full text-left"
          onClick={toggle}
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${keyName ?? 'array'}`}
        >
          {expanded ? (
            <ChevronDown className="h-3 w-3 mr-1 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3 w-3 mr-1 text-muted-foreground" />
          )}
          {keyName && <span className="text-purple-600 dark:text-purple-400">{keyName}: </span>}
          <span className="text-gray-500">[</span>
          {!expanded && (
            <>
              <span className="text-muted-foreground mx-1 text-xs">
                {value.length} item{value.length === 1 ? '' : 's'}
              </span>
              <span className="text-gray-500">]</span>
            </>
          )}
        </button>
        {expanded && (
          <>
            {value.map((item, index) => (
              <JsonNode
                key={index}
                keyName={String(index)}
                value={item}
                level={level + 1}
                defaultExpandLevel={defaultExpandLevel}
                previewFn={previewFn}
              />
            ))}
            <div style={{ paddingLeft: indent }} className="text-gray-500 py-0.5">
              ]
            </div>
          </>
        )}
      </div>
    );
  }

  // Object
  if (typeof value === 'object') {
    const entries = Object.entries(value);
    if (entries.length === 0) {
      return (
        <div style={{ paddingLeft: indent }} className="flex items-center py-0.5">
          <span className="text-muted-foreground mr-1">-</span>
          {keyName && <span className="text-purple-600 dark:text-purple-400">{keyName}: </span>}
          <span className="text-gray-500">{'{}'}</span>
        </div>
      );
    }

    const preview = customPreview ?? getPreview(value as Record<string, unknown>);

    return (
      <div>
        <button
          type="button"
          style={{ paddingLeft: indent }}
          className="flex items-center py-0.5 cursor-pointer hover:bg-muted/50 rounded w-full text-left"
          onClick={toggle}
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${keyName ?? 'object'}`}
        >
          {expanded ? (
            <ChevronDown className="h-3 w-3 mr-1 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3 w-3 mr-1 text-muted-foreground" />
          )}
          {keyName && <span className="text-purple-600 dark:text-purple-400">{keyName}: </span>}
          <span className="text-gray-500">{'{'}</span>
          {!expanded && (
            <>
              {preview ? (
                <span className="text-amber-600 dark:text-amber-400 mx-1 text-xs truncate max-w-md">
                  {preview}
                </span>
              ) : (
                <span className="text-muted-foreground mx-1 text-xs">
                  {entries.length} key{entries.length === 1 ? '' : 's'}
                </span>
              )}
              <span className="text-gray-500">{'}'}</span>
            </>
          )}
        </button>
        {expanded && (
          <>
            {entries.map(([key, value_]) => (
              <JsonNode
                key={key}
                keyName={key}
                value={value_}
                level={level + 1}
                defaultExpandLevel={defaultExpandLevel}
                previewFn={previewFn}
              />
            ))}
            <div style={{ paddingLeft: indent }} className="text-gray-500 py-0.5">
              {'}'}
            </div>
          </>
        )}
      </div>
    );
  }

  // String
  if (typeof value === 'string') {
    return (
      <div style={{ paddingLeft: indent }} className="flex items-start py-0.5">
        <span className="text-muted-foreground mr-1">-</span>
        {keyName && <span className="text-purple-600 dark:text-purple-400">{keyName}: </span>}
        <span className="text-green-600 dark:text-green-400 break-all">&quot;{value}&quot;</span>
      </div>
    );
  }

  // Number
  if (typeof value === 'number') {
    return (
      <div style={{ paddingLeft: indent }} className="flex items-center py-0.5">
        <span className="text-muted-foreground mr-1">-</span>
        {keyName && <span className="text-purple-600 dark:text-purple-400">{keyName}: </span>}
        <span className="text-blue-600 dark:text-blue-400">{value}</span>
      </div>
    );
  }

  // Boolean
  if (typeof value === 'boolean') {
    return (
      <div style={{ paddingLeft: indent }} className="flex items-center py-0.5">
        <span className="text-muted-foreground mr-1">-</span>
        {keyName && <span className="text-purple-600 dark:text-purple-400">{keyName}: </span>}
        <span className="text-orange-600 dark:text-orange-400">{value ? 'true' : 'false'}</span>
      </div>
    );
  }

  // Fallback
  return (
    <div style={{ paddingLeft: indent }} className="flex items-center py-0.5">
      <span className="text-muted-foreground mr-1">-</span>
      {keyName && <span className="text-purple-600 dark:text-purple-400">{keyName}: </span>}
      <span className="text-gray-600">{String(value)}</span>
    </div>
  );
}

/** Renders an interactive, collapsible tree view of arbitrary JSON data with syntax-colored value types. */
export function JsonViewer({
  data,
  defaultExpandLevel = 2,
  className,
  previewFn,
}: JsonViewerProps): React.JSX.Element {
  return (
    <div className={className ?? 'font-mono text-sm'}>
      <JsonNode
        value={data}
        level={0}
        defaultExpandLevel={defaultExpandLevel}
        previewFn={previewFn}
      />
    </div>
  );
}
